import React, { useContext, useState } from 'react';
import styled from '@emotion/styled';
import Divider from '@mui/material/Divider';
import Pagination from '@mui/material/Pagination';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import mainContext from '../../contexts/mainContext';
import { __constants, __jobStatus, __snackBarSeverity, __snackBarText } from '../../constants';
import { getJobsController } from '../../Controllers/BookingManagement';
import Filter from './commonFilter'; // Import the common filter

const ColumnCustomizer = ({ columns, visibleColumns, setVisibleColumns }) => {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleToggleColumn = (columnId) => {
    if (visibleColumns.includes(columnId)) {
      if (visibleColumns.length === 1) return; // Prevent hiding all columns
      setVisibleColumns(visibleColumns.filter(id => id !== columnId));
    } else {
      // Re-insert keeping original column order
      const newVisible = columns
        .filter(col => visibleColumns.includes(col.id) || col.id === columnId)
        .map(col => col.id);
      setVisibleColumns(newVisible);
    }
  };

  return (
    <>
      <Button
        size='small'
        sx={{ p: 0, minWidth: "30px" }}
        onClick={handleClick}
      >
        <Tooltip title="Customize Columns">
          <ViewColumnIcon />
        </Tooltip>
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          style: {
            maxHeight: 300,
            width: '220px',
            padding: '8px'
          },
        }}
      >
        <div style={{ fontWeight: 'bold', padding: '4px 8px 8px 8px', fontSize: '90%' }}>
          Show/Hide Columns
        </div>
        {columns.map((column) => (
          <MenuItem 
            key={column.id} 
            dense 
            onClick={() => handleToggleColumn(column.id)}
            style={{ padding: '2px 8px' }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={visibleColumns.includes(column.id)}
                  size="small"
                  onClick={(e) => e.stopPropagation()}
                  onChange={() => handleToggleColumn(column.id)}
                />
              }
              label={<span style={{ fontSize: '90%' }}>{column.label}</span>}
              style={{ margin: 0 }}
            />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

const CommonDataTable = ({
  type, // "Booking" or "Job"
  profileNavTitle,
  columns = [],
  viewColumnWidth = "0.8fr",
  onRowClick,
  canvasComponent: CanvasComponent,
  canvasState,
  setCanvasState,
  additionalParams = {}
}) => {
  const { setProfileNavTitle, setSnackBar, setPageload } = useContext(mainContext);
  const [filterState, setFilterState] = React.useState({});
  const [data, setData] = useState([]);
  const [metaData, setMetaData] = useState(null);
  
  const storageKey = `custom_columns_${type}`;

  // Track visible columns using their IDs
  const [visibleColumns, setVisibleColumns] = useState(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) {
          // Make sure all saved IDs exist in the current columns list
          const validIds = parsed.filter(id => columns.some(col => col.id === id));
          if (validIds.length > 0) {
            return validIds;
          }
        }
      }
    } catch (e) {
      console.error("Error reading visible columns from localStorage", e);
    }
    return columns.filter(col => col.isDefault).map(col => col.id);
  });

  const updateVisibleColumns = (newVisible) => {
    setVisibleColumns(newVisible);
    try {
      localStorage.setItem(storageKey, JSON.stringify(newVisible));
    } catch (e) {
      console.error("Error saving visible columns to localStorage", e);
    }
  };

  const callSnackBar = (msg, type) => {
    setSnackBar({
      status: true,
      text: msg,
      severity: __snackBarSeverity[type]
    });
  };

  const statusClassMap = {
    [__jobStatus.posted]: "status_pending",
    [__jobStatus.hired]: "status_processing",
    [__jobStatus.completed]: "status_processing",
    [__jobStatus.finalized]: "status_active",
    [__jobStatus.cancelled]: "status_cancel",
    [__jobStatus.expired]: "status_expired",
    [__jobStatus.declined]: "status_declined",
    [__jobStatus.refundByNona]: "status_refunded",
    [__jobStatus.partiallyRefundByNona]: "status_partially_refunded",
  };

  const getData = async (page = 1, filterApply = true) => {
    setPageload(true);
    let param = null;
    
    if (filterApply) {
      if (filterState.status) {
        param = { page: page, ...filterState, ...additionalParams, type: type };
      } else {
        param = { page: page, ...filterState, ...additionalParams, status: "", type: type };
      }
    } else {
      param = { page: page, ...additionalParams, status: "", type: type };
    }

    const res = await getJobsController(param);
    setPageload(false);
    
    if (res && res.status === 500) {
      return callSnackBar(__snackBarText.internalServerError, "error");
    }
    if (res.data.status !== 200) {
      return callSnackBar(res.data.errors[0].message, "error");
    }

    setData(res.data.data.allJobs[0].data);
    setMetaData(res.data.data.allJobs[0].metadata[0]);
  };

  const handlePaginationClick = (e, page) => {
    getData(page);
  };

  React.useEffect(() => {
    getData();
    setProfileNavTitle(profileNavTitle);
    // eslint-disable-next-line
  }, []);

  // Compute active columns and dynamic grid template
  const activeColumnsList = columns.filter(col => visibleColumns.includes(col.id));
  const dynamicGridColumns = activeColumnsList.map(col => col.width).join(" ") + " " + viewColumnWidth;

  return (
    <>
      {CanvasComponent && (
        <CanvasComponent
          canvasState={canvasState}
          setCanvasState={setCanvasState}
        />
      )}
      <Container className='scrollbar'>
        <Header>
          <ListGrid gridColumns={dynamicGridColumns} columnCount={activeColumnsList.length + 1}>
            {activeColumnsList.map((column) => (
              <li key={column.id} style={column.style || {}}>
                {column.label}
              </li>
            ))}
            <li style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "0.2rem" }}>
              <Filter
                setFilterState={setFilterState}
                filterState={filterState}
                callFunction={getData}
              />
              <ColumnCustomizer
                columns={columns}
                visibleColumns={visibleColumns}
                setVisibleColumns={updateVisibleColumns}
              />
            </li>
          </ListGrid>
        </Header>
        <Body>
          <Divider />
          {data.map((item, index) => (
            <ListGrid key={index} gridColumns={dynamicGridColumns} columnCount={activeColumnsList.length + 1}>
              {activeColumnsList.map((column) => (
                <React.Fragment key={column.id}>
                  {column.render(item, statusClassMap)}
                </React.Fragment>
              ))}
              <li className='list_card_view_btn' tabIndex={1} onClick={() => {
                if (onRowClick) {
                  onRowClick(item, setCanvasState);
                }
              }}>
                View
              </li>
            </ListGrid>
          ))}
        </Body>
        {data.length === 0 && <div className='no-record'><span>No Record Found</span></div>}
      </Container>
      {data.length > 0 && (
        <>
          <Pagination 
            count={Math.ceil((metaData?.total ?? 1) / __constants.listingLimit)} 
            color="primary" 
            shape="rounded" 
            onChange={(e, page) => { handlePaginationClick(e, page) }} 
          />
          <br />
        </>
      )}
    </>
  );
};

const Container = styled("div")`
  overflow-x: auto;
  padding-top: 1rem;
  margin-bottom: 1rem;
  font-size: 85%;
  min-height: 80vh;
`;

const Header = styled("div")`
  margin: 0.5rem 0;
  ul li{
    font-family: "semi-bold" !important;
  }
`;

const Body = styled("div")`
  margin-top: 1rem;
  display: grid;
  row-gap: 1.6rem;
`;

const ListGrid = styled("ul")`
  min-width: ${props => props.columnCount ? `${Math.max(1200, props.columnCount * 160)}px` : "1200px"};
  display: grid;
  column-gap: 0.7rem;
  grid-template-columns: ${props => props.gridColumns};
  align-items: center;
  padding: 3px !important;
  &:hover{
    background-color: rgba(0,0,0,0.04);
    border-radius: 8px;
  }
`;

export default CommonDataTable;